import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import {
  FiSearch,
  FiEdit3,
  FiCamera,
  FiMonitor,
  FiTrendingUp,
  FiArrowUpRight,
} from "react-icons/fi";
import "./CreativeProcess.css";

gsap.registerPlugin(ScrollTrigger);

const stepIcons = [
  FiSearch,
  FiEdit3,
  FiCamera,
  FiMonitor,
  FiTrendingUp,
];

const processSteps = [
  {
    number: "01",
    title: "Discover",
    label: "Understanding The Objective",
    description:
      "We begin by understanding your brand, audience, objectives and the opportunity behind the project.",
    meta: "STRATEGY / RESEARCH",
  },
  {
    number: "02",
    title: "Define",
    label: "Building The Direction",
    description:
      "Insights become a focused creative direction, defining the message, visual language and production approach.",
    meta: "CONCEPT / DIRECTION",
  },
  {
    number: "03",
    title: "Create",
    label: "Bringing Ideas To Life",
    description:
      "Our production process transforms the direction into carefully crafted visuals, films and content experiences.",
    meta: "PRODUCTION / EXECUTION",
  },
  {
    number: "04",
    title: "Refine",
    label: "Shaping Every Detail",
    description:
      "Editing, sound, color and finishing are refined to create a cohesive final experience across every deliverable.",
    meta: "POST / DELIVERY",
  },
  {
    number: "05",
    title: "Grow",
    label: "Creating Long-Term Value",
    description:
      "We help extend the work into campaigns and ongoing content systems that continue building brand value.",
    meta: "PARTNERSHIP / GROWTH",
  },
];

function CreativeProcess() {
  const sectionRef = useRef(null);
  const pinRef = useRef(null);
  const viewportRef = useRef(null);
  const trackRef = useRef(null);
  const progressRef = useRef(null);

  useLayoutEffect(() => {
    const section = sectionRef.current;
    const pin = pinRef.current;
    const viewport = viewportRef.current;
    const track = trackRef.current;
    const progress = progressRef.current;

    if (!section || !pin || !viewport || !track || !progress) {
      return undefined;
    }

    const context = gsap.context(() => {
      gsap.set(track, {
        x: 0,
      });

      gsap.set(progress, {
        scaleX: 0,
        transformOrigin: "left center",
      });

      const getScrollDistance = () => {
        return Math.max(
          0,
          track.scrollWidth - viewport.clientWidth
        );
      };

      const horizontalTween = gsap.to(track, {
        x: () => -getScrollDistance(),
        ease: "none",
        paused: true,
        invalidateOnRefresh: true,
      });

      const trigger = ScrollTrigger.create({
        id: "creative-process-horizontal",
        trigger: section,
        start: "top top",
        end: () => {
          const distance = getScrollDistance();

          return `+=${distance + window.innerHeight * 0.6}`;
        },
        pin: pin,
        pinSpacing: true,
        scrub: 1,
        anticipatePin: 1,
        invalidateOnRefresh: true,
        animation: horizontalTween,

        onUpdate: (self) => {
          gsap.set(progress, {
            scaleX: self.progress,
          });
        },

        onRefresh: () => {
          gsap.set(progress, {
            transformOrigin: "left center",
          });
        },
      });

      return () => {
        trigger.kill();
        horizontalTween.kill();
      };
    }, section);

    const refreshScrollTrigger = () => {
      ScrollTrigger.refresh();
    };

    const refreshTimer = window.setTimeout(() => {
      ScrollTrigger.refresh();
    }, 300);

    window.addEventListener(
      "resize",
      refreshScrollTrigger
    );

    window.addEventListener(
      "orientationchange",
      refreshScrollTrigger
    );

    return () => {
      window.clearTimeout(refreshTimer);

      window.removeEventListener(
        "resize",
        refreshScrollTrigger
      );

      window.removeEventListener(
        "orientationchange",
        refreshScrollTrigger
      );

      context.revert();
    };
  }, []);

  return (
    <section
      className="creative-process"
      id="process"
      ref={sectionRef}
    >
      <div
        className="creative-process-pin"
        ref={pinRef}
      >
        <div className="creative-process-container">
          <div className="creative-process-intro">
            <h2>
              THE PROCESS
              <span>BEHIND THE WORK</span>
            </h2>

            <p>
              How End-to-End Production works
            </p>
          </div>

          <div
            className="creative-process-viewport"
            ref={viewportRef}
          >
            <div
              className="creative-process-track"
              ref={trackRef}
            >
              {processSteps.map((step, index) => {
                const StepIcon = stepIcons[index];

                return (
                  <article
                    className="creative-process-card"
                    key={step.number}
                  >
                    <span className="creative-process-bg-number">
                      {step.number}
                    </span>

                    <div className="creative-process-card-header">
                      <div className="creative-process-icon">
                        <StepIcon />
                      </div>

                      <span className="creative-process-step">
                        STEP {step.number}
                      </span>
                    </div>

                    <div className="creative-process-card-content">
                      <span className="creative-process-card-label">
                        {step.label}
                      </span>

                      <h3>
                        {step.title}
                      </h3>

                      <p>
                        {step.description}
                      </p>
                    </div>

                    <div className="creative-process-card-footer">
                      <span>
                        {step.meta}
                      </span>

                      <button
                        type="button"
                        className="creative-process-btn"
                      >
                        Learn More
                        <FiArrowUpRight />
                      </button>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <div className="creative-process-navigation">
            <span>
              SCROLL TO EXPLORE
            </span>

            <div className="creative-process-progress">
              <span ref={progressRef} />
            </div>
          </div>
        </div>

        <div className="creative-process-glow" />
      </div>
    </section>
  );
}

export default CreativeProcess;